import { useState, useEffect } from 'react';
import { APP_VERSION } from '../version';

// This version gets baked into the JS bundle at build time
const CURRENT_VERSION = APP_VERSION;
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_RELOAD_IDLE_MS = 30 * 1000;
const AUTO_RELOAD_KEY_PREFIX = 'dashboard-auto-reload:';
// A reload that came back on the same stale bundle (cached shell, deploy in
// progress) is tried again after this long, up to this many times per version.
// Before 2026-09-23 one attempt per version was final: a tab that reloaded into
// the old shell stayed on old code until the next deploy (incident item K).
const AUTO_RELOAD_RETRY_MS = 10 * 60 * 1000;
const AUTO_RELOAD_MAX_ATTEMPTS = 6;
const DEFAULT_RELOAD = () => window.location.reload();

function readReloadAttempts(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    if (raw === '1') return { attempts: 1, lastAt: 0 }; // pre-retry format: one attempt, long ago
    const parsed = JSON.parse(raw);
    return Number.isFinite(parsed?.attempts) && Number.isFinite(parsed?.lastAt) ? parsed : null;
  } catch {
    return null;
  }
}

function writeReloadAttempts(key, record) {
  try { sessionStorage.setItem(key, JSON.stringify(record)); } catch { /* storage unavailable */ }
}

export function reloadAllowed(record, now = Date.now()) {
  if (!record) return true;
  if (record.attempts >= AUTO_RELOAD_MAX_ATTEMPTS) return false;
  return now - record.lastAt >= AUTO_RELOAD_RETRY_MS;
}

function isEditableElement(element) {
  if (!element) return false;
  if (element.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
}

export function useVersionCheck({ reload = DEFAULT_RELOAD } = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let mounted = true;
    let reloadTimer = null;
    const updateVersion = { current: null };
    const lastActivity = { current: Date.now() };
    const reloadAttempted = { current: false };

    const markActivity = () => {
      lastActivity.current = Date.now();
      scheduleReload();
    };

    const reloadIfSafe = () => {
      if (!mounted || !updateVersion.current || reloadAttempted.current) return;
      const idleLongEnough = Date.now() - lastActivity.current >= AUTO_RELOAD_IDLE_MS;
      const safeToReload = document.visibilityState === 'hidden'
        || (idleLongEnough && !isEditableElement(document.activeElement));
      if (!safeToReload) {
        scheduleReload();
        return;
      }

      const key = `${AUTO_RELOAD_KEY_PREFIX}${updateVersion.current}`;
      const record = readReloadAttempts(key);
      if (!reloadAllowed(record)) return;
      writeReloadAttempts(key, { attempts: (record?.attempts || 0) + 1, lastAt: Date.now() });
      reloadAttempted.current = true;
      reload();
    };

    function scheduleReload() {
      if (!updateVersion.current || reloadAttempted.current) return;
      if (reloadTimer) clearTimeout(reloadTimer);
      const waitForIdle = Math.max(0, AUTO_RELOAD_IDLE_MS - (Date.now() - lastActivity.current));
      reloadTimer = setTimeout(reloadIfSafe, document.visibilityState === 'hidden' ? 0 : waitForIdle);
    }

    const checkVersion = async () => {
      try {
        // Add cache-busting query param to always get fresh version.json
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) return;

        const data = await response.json();

        if (data.version && data.version !== CURRENT_VERSION && mounted) {
          updateVersion.current = data.version;
          setUpdateAvailable(true);
          scheduleReload();
        }
      } catch (error) {
        // Silently fail - version check is not critical
        // Version check failed - not critical
      }
    };

    // Check on mount
    checkVersion();

    // Check every 5 minutes, plus when a suspended/hidden tab becomes active.
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion();
      scheduleReload();
    };
    window.addEventListener('focus', checkVersion);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pointerdown', markActivity, { passive: true });
    window.addEventListener('keydown', markActivity, { passive: true });
    window.addEventListener('input', markActivity, { passive: true });

    return () => {
      mounted = false;
      clearInterval(interval);
      if (reloadTimer) clearTimeout(reloadTimer);
      window.removeEventListener('focus', checkVersion);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pointerdown', markActivity);
      window.removeEventListener('keydown', markActivity);
      window.removeEventListener('input', markActivity);
    };
  }, [reload]);

  const refresh = () => {
    window.location.reload();
  };

  return { updateAvailable, refresh };
}
